import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, TextField, MenuItem, Chip, Stack, useTheme, FormControl, InputLabel, Select,
  FormControlLabel, Checkbox,
} from '@mui/material';
import {
  Add, LabelOutlined, EmojiEvents, Info as InfoIcon, Warning as WarningIcon, Receipt, Block,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import letterCategoryService from '../../services/letterCategoryService';
import { LetterCategory, LetterCategorySeverity } from '../../types/letters';
import { severityChipSx, severityTone } from '../../utils/severityTone';
import SettingsList, { SettingsTile } from '../common/SettingsList';
import SettingsDialog from '../common/SettingsDialog';

const SEVERITY_OPTIONS: { value: LetterCategorySeverity; label: string }[] = [
  { value: 'positive', label: 'Positive' },
  { value: 'neutral',  label: 'Neutral' },
  { value: 'caution',  label: 'Caution' },
  { value: 'critical', label: 'Critical' },
];

// Icon name → MUI component. Kept narrow on purpose — adding more icons is one
// line. The string lives on the backend so categories can ship icons without
// touching the FE; unknown icons fall back to the generic label tag.
const ICON_MAP: Record<string, React.ReactNode> = {
  EmojiEvents: <EmojiEvents fontSize="small" />,
  Info: <InfoIcon fontSize="small" />,
  Warning: <WarningIcon fontSize="small" />,
  Receipt: <Receipt fontSize="small" />,
  Block: <Block fontSize="small" />,
};
const renderCategoryIcon = (icon?: string) => ICON_MAP[icon || ''] || <LabelOutlined fontSize="small" />;

const requiredFields = (c: LetterCategory) =>
  c.requiresAmount && c.requiresReason ? 'Needs amount and reason'
    : c.requiresAmount ? 'Needs an amount'
      : c.requiresReason ? 'Needs a reason'
        : 'Title only';

const categorySchema = yup.object({
  code: yup.string().required('Code is required').max(40).matches(/^[a-z0-9_]+$/i, 'Use letters, numbers, underscores'),
  label: yup.string().required('Label is required').max(80),
  severity: yup.string().oneOf(['positive', 'neutral', 'caution', 'critical']).required(),
  requiresAmount: yup.boolean(),
  requiresReason: yup.boolean(),
  icon: yup.string().nullable(),
  sortOrder: yup.number().min(0),
});

interface FormState {
  code: string;
  label: string;
  severity: LetterCategorySeverity;
  requiresAmount: boolean;
  requiresReason: boolean;
  icon: string;
  sortOrder: number;
}

const EMPTY: FormState = {
  code: '',
  label: '',
  severity: 'neutral',
  requiresAmount: false,
  requiresReason: false,
  icon: '',
  sortOrder: 0,
};

const LetterCategoriesSettings: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const hasWrite = canWrite('settings_letter_categories');

  const [categories, setCategories] = useState<LetterCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LetterCategory | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await letterCategoryService.listLetterCategories();
      setCategories(list);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || err.message || 'Could not load letter categories', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const formik = useFormik<FormState>({
    initialValues: EMPTY,
    enableReinitialize: true,
    validationSchema: categorySchema,
    onSubmit: async (values) => {
      try {
        if (editing) {
          await letterCategoryService.updateLetterCategory(editing._id, {
            label: values.label,
            severity: values.severity,
            requiresAmount: values.requiresAmount,
            requiresReason: values.requiresReason,
            icon: values.icon || undefined,
            sortOrder: values.sortOrder,
          } as any);
          enqueueSnackbar('Category saved', { variant: 'success' });
        } else {
          await letterCategoryService.createLetterCategory({
            code: values.code.trim().toLowerCase(),
            label: values.label.trim(),
            severity: values.severity,
            requiresAmount: values.requiresAmount,
            requiresReason: values.requiresReason,
            icon: values.icon || undefined,
            sortOrder: values.sortOrder,
          });
          enqueueSnackbar('Category added', { variant: 'success' });
        }
        setDialogOpen(false);
        setEditing(null);
        await load();
      } catch (err: any) {
        enqueueSnackbar(err?.response?.data?.message || err.message || 'Could not save this category', { variant: 'error' });
      }
    }
  });

  const openCreate = () => {
    setEditing(null);
    formik.resetForm({ values: EMPTY });
    setDialogOpen(true);
  };

  const openEdit = (category: LetterCategory) => {
    setEditing(category);
    formik.resetForm({
      values: {
        code: category.code,
        label: category.label,
        severity: category.severity,
        requiresAmount: category.requiresAmount,
        requiresReason: category.requiresReason,
        icon: category.icon || '',
        sortOrder: category.sortOrder || 0,
      }
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  return (
    <>
      <SettingsList
        rows={categories}
        loading={loading}
        rowKey={c => c._id}
        name={c => c.label}
        leading={c => (
          <SettingsTile color={severityTone(theme, c.severity).main}>{renderCategoryIcon(c.icon)}</SettingsTile>
        )}
        subtitle={c => (
          <>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>{c.code}</Box>
            {` · ${requiredFields(c)}`}
          </>
        )}
        meta={c => (
          <Chip
            size="small"
            label={SEVERITY_OPTIONS.find(o => o.value === c.severity)?.label ?? c.severity}
            sx={{ ...severityChipSx(theme, c.severity), height: 22, fontSize: 11 }}
          />
        )}
        searchText={c => `${c.label} ${c.code}`}
        canWrite={hasWrite}
        onEdit={openEdit}
        remove={{
          run: c => letterCategoryService.deleteLetterCategory(c._id),
          noun: 'category',
          verb: 'Archive',
          consequence: c => `"${c.label}" will no longer appear in the issue dialog. Existing letters in this category remain visible.`,
        }}
        reload={load}
        empty={{
          icon: <LabelOutlined />,
          title: 'No letter categories yet',
          description: 'Categories are the kinds of letters you issue to students, such as a commendation or a fine.',
        }}
        toolbar={hasWrite && (
          <Button variant="contained" disableElevation startIcon={<Add />} onClick={openCreate}>
            Add category
          </Button>
        )}
      />

      <SettingsDialog
        open={dialogOpen}
        title={editing ? 'Edit letter category' : 'New letter category'}
        onClose={closeDialog}
        onSave={formik.submitForm}
        saving={formik.isSubmitting}
        saveLabel={editing ? 'Save' : 'Add category'}
      >
        <TextField
          fullWidth
          label="Code"
          name="code"
          disabled={!!editing}
          helperText={editing
            ? 'Code cannot be changed after creation'
            : (formik.touched.code && formik.errors.code) || 'Lowercase identifier (e.g. detention, parent_meeting)'}
          value={formik.values.code}
          onChange={e => formik.setFieldValue('code', e.target.value.toLowerCase())}
          error={formik.touched.code && Boolean(formik.errors.code)}
        />
        <TextField
          fullWidth
          label="Label"
          name="label"
          placeholder="e.g. Detention Letter"
          value={formik.values.label}
          onChange={formik.handleChange}
          error={formik.touched.label && Boolean(formik.errors.label)}
          helperText={formik.touched.label && formik.errors.label as string}
        />
        <FormControl fullWidth>
          <InputLabel>Severity</InputLabel>
          <Select
            name="severity"
            value={formik.values.severity}
            onChange={formik.handleChange}
            label="Severity"
          >
            {SEVERITY_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: severityTone(theme, o.value).main }} />
                  <span>{o.label}</span>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Icon</InputLabel>
          <Select
            name="icon"
            value={formik.values.icon}
            onChange={formik.handleChange}
            label="Icon"
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {Object.keys(ICON_MAP).map(k => (
              <MenuItem key={k} value={k}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {ICON_MAP[k]}<span>{k}</span>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={<Checkbox name="requiresAmount" checked={formik.values.requiresAmount} onChange={formik.handleChange} />}
            label="Requires amount"
          />
          <FormControlLabel
            control={<Checkbox name="requiresReason" checked={formik.values.requiresReason} onChange={formik.handleChange} />}
            label="Requires reason"
          />
        </Stack>
        <TextField
          fullWidth
          label="Sort order"
          name="sortOrder"
          type="number"
          value={formik.values.sortOrder}
          onChange={formik.handleChange}
          helperText="Lower numbers appear first in the picker"
        />
      </SettingsDialog>
    </>
  );
};

export default LetterCategoriesSettings;
